import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SchemaField } from "@/types/schemaBuilder";
import {
  Plus,
  Trash,
  ChevronUp,
  ChevronDown,
  Copy,
  Info,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FieldEditorProps {
  schema: Record<string, SchemaField>;
  onSchemaUpdate: (schema: Record<string, SchemaField>) => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  schema,
  onSchemaUpdate,
}) => {
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<string | null>(null);

  const generateFieldNameFromTitle = (
    title: string,
    existingNames: string[] = [],
  ): string => {
    let fieldName = title
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .replace(/_{2,}/g, "_");

    if (!/^[a-z_]/.test(fieldName)) {
      fieldName = "_" + fieldName;
    }

    if (!fieldName) {
      fieldName = "field";
    }

    let uniqueFieldName = fieldName;
    let counter = 1;
    while (existingNames.includes(uniqueFieldName)) {
      uniqueFieldName = `${fieldName}_${counter}`;
      counter++;
    }

    return uniqueFieldName;
  };

  const addField = () => {
    let fieldName = generateFieldNameFromTitle("newField", Object.keys(schema));
    const newField: SchemaField = {
      name: fieldName,
      type: "string",
      title: "New Field",
      description: "",
      required: false,
      order: Object.keys(schema).length, // Add order property for new fields
    };

    onSchemaUpdate({ ...schema, [fieldName]: newField });
    setExpandedField(fieldName);
    setActiveField(fieldName);
  };

  const updateField = (fieldName: string, updatedField: SchemaField) => {
    const updatedSchema = { ...schema };
    updatedSchema[fieldName] = updatedField;
    onSchemaUpdate(updatedSchema);
  };

  const deleteField = (fieldName: string) => {
    const updatedSchema = { ...schema };
    delete updatedSchema[fieldName];
    onSchemaUpdate(updatedSchema);
    if (expandedField === fieldName) {
      setExpandedField(null);
    }
  };

  const duplicateField = (fieldName: string) => {
    const originalField = schema[fieldName];
    let newFieldName = `${fieldName}Copy`;
    let counter = 1;
    while (schema[newFieldName]) {
      newFieldName = `${fieldName}Copy${counter}`;
      counter++;
    }

    const newField = {
      ...originalField,
      name: newFieldName,
      order: Object.keys(schema).length, // Set order for duplicated field
    };
    onSchemaUpdate({ ...schema, [newFieldName]: newField });
  };

  const moveField = (fieldName: string, direction: "up" | "down") => {
    const fieldNames = Object.keys(schema);
    const currentIndex = fieldNames.indexOf(fieldName);
    if (
      (direction === "up" && currentIndex === 0) ||
      (direction === "down" && currentIndex === fieldNames.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    const targetFieldName = fieldNames[newIndex];

    const updatedSchema: Record<string, SchemaField> = {};
    fieldNames.forEach((name) => {
      if (name === fieldName || name === targetFieldName) {
        return;
      }
      updatedSchema[name] = schema[name];
    });

    if (direction === "up") {
      updatedSchema[fieldName] = {
        ...schema[fieldName],
        order: newIndex,
      };
      updatedSchema[targetFieldName] = {
        ...schema[targetFieldName],
        order: currentIndex,
      };
    } else {
      updatedSchema[targetFieldName] = {
        ...schema[targetFieldName],
        order: currentIndex,
      };
      updatedSchema[fieldName] = {
        ...schema[fieldName],
        order: newIndex,
      };
    }

    onSchemaUpdate(updatedSchema);
  };

  const handleFieldNameChange = (oldName: string, newName: string) => {
    if (newName && newName !== oldName && !schema[newName]) {
      const updatedSchema = { ...schema };
      const field = { ...updatedSchema[oldName], name: newName };
      delete updatedSchema[oldName];
      updatedSchema[newName] = field;
      onSchemaUpdate(updatedSchema);
      setExpandedField(newName);
      setActiveField(newName);
    }
  };

  const handleFieldUpdate = (
    fieldName: string,
    field: SchemaField,
    property: string,
    value: any,
  ) => {
    const updatedField = { ...field };

    if (property.includes(".")) {
      const [parent, child] = property.split(".");
      updatedField[parent] = {
        ...(updatedField[parent] || {}),
        [child]: value,
      };
    } else {
      updatedField[property] = value;
    }

    updateField(fieldName, updatedField);
  };

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end event to reorder fields
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = Object.keys(schema).indexOf(active.id as string);
      const newIndex = Object.keys(schema).indexOf(over.id as string);

      // Create a new ordered schema object
      const orderedKeys = arrayMove(Object.keys(schema), oldIndex, newIndex);

      // Create a new schema object with the updated order
      // Use an ordered object to maintain the field order
      const newSchema = {};

      // Add order property to each field to preserve the order when saving
      orderedKeys.forEach((key, index) => {
        newSchema[key] = {
          ...schema[key],
          order: index, // Add explicit order property
        };
      });

      onSchemaUpdate(newSchema);
    }
  };

  return (
    <Card className="flex flex-col h-full">
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold">Field Editor</h3>
        <p className="text-sm text-muted-foreground">
          Create and configure form fields to generate your JSON schema.
        </p>
      </div>

      <div className="p-4 border-b">
        <Button onClick={addField}>
          <Plus className="mr-2 h-4 w-4" /> Add Field
        </Button>
      </div>

      <ScrollArea className="flex-1 p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={Object.keys(schema)}>
            <Accordion
              type="single"
              collapsible
              value={expandedField || undefined}
              onValueChange={(value) => setExpandedField(value)}
              className="space-y-4"
            >
              {Object.entries(schema).map(([fieldName, field]) => (
                <SortableFieldItem
                  key={fieldName}
                  id={fieldName}
                  field={field}
                  fieldName={fieldName}
                  isActive={activeField === fieldName}
                  isExpanded={expandedField === fieldName}
                  onDelete={deleteField}
                  onDuplicate={duplicateField}
                  onMoveUp={(name) => moveField(name, "up")}
                  onMoveDown={(name) => moveField(name, "down")}
                  onFieldUpdate={handleFieldUpdate}
                  onFieldNameChange={handleFieldNameChange}
                  generateFieldNameFromTitle={generateFieldNameFromTitle}
                  schema={schema}
                />
              ))}
            </Accordion>
          </SortableContext>
        </DndContext>

        {Object.keys(schema).length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No fields added yet. Click "Add Field" to get started.
            </p>
            <Button onClick={addField}>
              <Plus className="mr-2 h-4 w-4" /> Add Field
            </Button>
          </div>
        )}
      </ScrollArea>
    </Card>
  );
};

interface SortableFieldItemProps {
  id: string;
  field: SchemaField;
  fieldName: string;
  isActive: boolean;
  isExpanded: boolean;
  onDelete: (fieldName: string) => void;
  onDuplicate: (fieldName: string) => void;
  onMoveUp: (fieldName: string) => void;
  onMoveDown: (fieldName: string) => void;
  onFieldUpdate: (
    fieldName: string,
    field: SchemaField,
    property: string,
    value: any,
  ) => void;
  onFieldNameChange: (oldName: string, newName: string) => void;
  generateFieldNameFromTitle: (
    title: string,
    existingNames?: string[],
  ) => string;
  schema: Record<string, SchemaField>;
}

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
  id,
  field,
  fieldName,
  isActive,
  isExpanded,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onFieldUpdate,
  onFieldNameChange,
  generateFieldNameFromTitle,
  schema,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
    position: isDragging ? "relative" : "static",
  } as React.CSSProperties;

  return (
    <AccordionItem
      ref={setNodeRef}
      style={style}
      value={fieldName}
      className={`flex-col items-center justify-between p-4 mb-2 ${
        isActive ? "bg-primary/10" : "bg-muted/50"
      } border rounded-md ${
        isDragging
          ? "border-primary shadow-lg"
          : "border-border"
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div
            {...attributes}
            {...listeners}
            className="mr-2 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted dark:hover:bg-muted"
            title="Drag to reorder"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          <AccordionTrigger className="hover:no-underline">
            <span className="font-medium">{field.title || fieldName}</span>
          </AccordionTrigger>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(fieldName);
            }}
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(fieldName);
            }}
            title="Delete"
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <AccordionContent className="p-4 pt-2 border-t">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${fieldName}-name`}>Field Name</Label>
              <Input
                id={`${fieldName}-name`}
                defaultValue={fieldName}
                onBlur={(e) => {
                  onFieldNameChange(fieldName, e.target.value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${fieldName}-type`}>Field Type</Label>
              <Select
                defaultValue={field.type}
                onValueChange={(value: any) => {
                  const updatedField = { ...field, type: value };
                  if (value === "object") {
                    updatedField.properties = {};
                  } else if (value === "array") {
                    updatedField.items = {
                      name: `${fieldName}Item`,
                      type: "string",
                    };
                  } else {
                    delete updatedField.properties;
                    delete updatedField.items;
                  }
                  updateField(fieldName, updatedField);
                }}
              >
                <SelectTrigger id={`${fieldName}-type`}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="object">Object</SelectItem>
                  <SelectItem value="array">Array</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Label htmlFor={`${fieldName}-title`}>Display Label</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      The display label is used to generate the field name
                      automatically. Changing this will update the field name.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id={`${fieldName}-title`}
              value={field.title || ""}
              onChange={(e) => {
                const updatedField = {
                  ...field,
                  title: e.target.value,
                };
                updateField(fieldName, updatedField);
              }}
              onBlur={(e) => {
                if (e.target.value) {
                  const newFieldName = generateFieldNameFromTitle(
                    e.target.value,
                    Object.keys(schema).filter((name) => name !== fieldName),
                  );

                  if (newFieldName !== fieldName) {
                    handleFieldNameChange(fieldName, newFieldName);
                  }
                }
              }}
              placeholder="Display label for the field"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${fieldName}-description`}>Description</Label>
            <Textarea
              id={`${fieldName}-description`}
              defaultValue={field.description || ""}
              onBlur={(e) =>
                handleFieldUpdate(
                  fieldName,
                  field,
                  "description",
                  e.target.value,
                )
              }
              placeholder="Description or help text"
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id={`${fieldName}-required`}
              checked={field.required || false}
              onCheckedChange={(checked) =>
                handleFieldUpdate(fieldName, field, "required", checked)
              }
            />
            <Label htmlFor={`${fieldName}-required`}>Required field</Label>
          </div>

          {field.type === "string" && (
            <div className="space-y-4 border-t pt-4 mt-4">
              <h4 className="font-medium">String Validation</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`${fieldName}-minLength`}>Min Length</Label>
                  <Input
                    id={`${fieldName}-minLength`}
                    type="number"
                    defaultValue={field.validation?.minLength || ""}
                    onBlur={(e) => {
                      const value = e.target.value
                        ? parseInt(e.target.value)
                        : undefined;
                      handleFieldUpdate(
                        fieldName,
                        field,
                        "validation.minLength",
                        value,
                      );
                    }}
                    placeholder="Minimum length"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldName}-maxLength`}>Max Length</Label>
                  <Input
                    id={`${fieldName}-maxLength`}
                    type="number"
                    defaultValue={field.validation?.maxLength || ""}
                    onBlur={(e) => {
                      const value = e.target.value
                        ? parseInt(e.target.value)
                        : undefined;
                      handleFieldUpdate(
                        fieldName,
                        field,
                        "validation.maxLength",
                        value,
                      );
                    }}
                    placeholder="Maximum length"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor={`${fieldName}-pattern`}>Pattern (Regex)</Label>
                <Input
                  id={`${fieldName}-pattern`}
                  defaultValue={field.validation?.pattern || ""}
                  onBlur={(e) =>
                    handleFieldUpdate(
                      fieldName,
                      field,
                      "validation.pattern",
                      e.target.value,
                    )
                  }
                  placeholder="Regular expression pattern"
                />
              </div>
            </div>
          )}

          {field.type === "number" && (
            <div className="space-y-4 border-t pt-4 mt-4">
              <h4 className="font-medium">Number Validation</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`${fieldName}-minimum`}>Minimum</Label>
                  <Input
                    id={`${fieldName}-minimum`}
                    type="number"
                    defaultValue={field.validation?.minimum || ""}
                    onBlur={(e) => {
                      const value = e.target.value
                        ? parseFloat(e.target.value)
                        : undefined;
                      handleFieldUpdate(
                        fieldName,
                        field,
                        "validation.minimum",
                        value,
                      );
                    }}
                    placeholder="Minimum value"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldName}-maximum`}>Maximum</Label>
                  <Input
                    id={`${fieldName}-maximum`}
                    type="number"
                    defaultValue={field.validation?.maximum || ""}
                    onBlur={(e) => {
                      const value = e.target.value
                        ? parseFloat(e.target.value)
                        : undefined;
                      handleFieldUpdate(
                        fieldName,
                        field,
                        "validation.maximum",
                        value,
                      );
                    }}
                    placeholder="Maximum value"
                  />
                </div>
              </div>
            </div>
          )}

          {field.type === "object" && (
            <div className="space-y-4 border-t pt-4 mt-4">
              <h4 className="font-medium">Object Properties</h4>
              <p className="text-sm text-muted-foreground">
                Nested objects are not supported in the UI editor yet. Use the
                JSON Schema tab to manually edit nested objects.
              </p>
            </div>
          )}

          {field.type === "array" && (
            <div className="space-y-4 border-t pt-4 mt-4">
              <h4 className="font-medium">Array Items</h4>
              <div className="space-y-2">
                <Label htmlFor={`${fieldName}-items-type`}>Item Type</Label>
                <Select
                  defaultValue={field.items?.type || "string"}
                  onValueChange={(value: any) => {
                    const newItems = {
                      ...field.items,
                      type: value,
                      name: `${fieldName}Item`,
                    };

                    if (value === "object" && !newItems.properties) {
                      newItems.properties = {};
                    }

                    handleFieldUpdate(fieldName, field, "items", newItems);
                  }}
                >
                  <SelectTrigger id={`${fieldName}-items-type`}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">String</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="object">Object</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {field.items?.type === "object" && (
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`${fieldName}-items-sortable`}
                      checked={field.items.sortable || false}
                      onCheckedChange={(checked) => {
                        const newItems = {
                          ...field.items,
                          sortable: checked,
                        };
                        onFieldUpdate(fieldName, field, "items", newItems);
                      }}
                    />
                    <Label htmlFor={`${fieldName}-items-sortable`}>
                      Enable drag-and-drop sorting
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`${fieldName}-items-viewable`}
                      checked={field.items.viewable || false}
                      onCheckedChange={(checked) => {
                        const newItems = {
                          ...field.items,
                          viewable: checked,
                        };
                        onFieldUpdate(fieldName, field, "items", newItems);
                      }}
                    />
                    <Label htmlFor={`${fieldName}-items-viewable`}>
                      Enable view/edit modes
                    </Label>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`${fieldName}-minItems`}>Min Items</Label>
                  <Input
                    id={`${fieldName}-minItems`}
                    type="number"
                    defaultValue={field.validation?.minItems || ""}
                    onBlur={(e) => {
                      const value = e.target.value
                        ? parseInt(e.target.value)
                        : undefined;
                      handleFieldUpdate(
                        fieldName,
                        field,
                        "validation.minItems",
                        value,
                      );
                    }}
                    placeholder="Minimum items"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldName}-maxItems`}>Max Items</Label>
                  <Input
                    id={`${fieldName}-maxItems`}
                    type="number"
                    defaultValue={field.validation?.maxItems || ""}
                    onBlur={(e) => {
                      const value = e.target.value
                        ? parseInt(e.target.value)
                        : undefined;
                      handleFieldUpdate(
                        fieldName,
                        field,
                        "validation.maxItems",
                        value,
                      );
                    }}
                    placeholder="Maximum items"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id={`${fieldName}-uniqueItems`}
                  checked={field.validation?.uniqueItems || false}
                  onCheckedChange={(checked) =>
                    handleFieldUpdate(
                      fieldName,
                      field,
                      "validation.uniqueItems",
                      checked,
                    )
                  }
                />
                <Label htmlFor={`${fieldName}-uniqueItems`}>
                  Require unique items
                </Label>
              </div>

              {field.items?.type === "object" && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="font-medium mb-2">Object Properties</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Define the properties for each object in this array
                  </p>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const properties = field.items?.properties || {};
                      let propName = "property";
                      let counter = 1;
                      while (properties[propName]) {
                        propName = `property${counter}`;
                        counter++;
                      }

                      const newProp = {
                        name: propName,
                        type: "string",
                        title: "New Property",
                      };

                      const newProperties = {
                        ...properties,
                        [propName]: newProp,
                      };

                      const newItems = {
                        ...field.items,
                        properties: newProperties,
                      };

                      handleFieldUpdate(fieldName, field, "items", newItems);
                    }}
                    className="mb-4"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Property
                  </Button>

                  {field.items?.properties &&
                  Object.entries(field.items.properties).length > 0 ? (
                    <div className="space-y-4 border rounded-md p-4">
                      {Object.entries(field.items.properties).map(
                        ([propName, propField]) => (
                          <div
                            key={propName}
                            className="border-b pb-4 last:border-0 last:pb-0"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <h5 className="font-medium">
                                {propField.title || propName}
                              </h5>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const newProperties = {
                                    ...field.items?.properties,
                                  };
                                  delete newProperties[propName];

                                  const newItems = {
                                    ...field.items,
                                    properties: newProperties,
                                  };

                                  handleFieldUpdate(
                                    fieldName,
                                    field,
                                    "items",
                                    newItems,
                                  );
                                }}
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-2">
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`${fieldName}-${propName}-name`}
                                >
                                  Property Name
                                </Label>
                                <Input
                                  id={`${fieldName}-${propName}-name`}
                                  defaultValue={propName}
                                  onBlur={(e) => {
                                    const newPropName = e.target.value;
                                    if (
                                      newPropName &&
                                      newPropName !== propName &&
                                      !field.items?.properties?.[newPropName]
                                    ) {
                                      const newProperties = {
                                        ...field.items?.properties,
                                      };
                                      const propData = {
                                        ...newProperties[propName],
                                        name: newPropName,
                                      };
                                      delete newProperties[propName];
                                      newProperties[newPropName] = propData;

                                      const newItems = {
                                        ...field.items,
                                        properties: newProperties,
                                      };

                                      onFieldUpdate(
                                        fieldName,
                                        field,
                                        "items",
                                        newItems,
                                      );
                                    }
                                  }}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label
                                  htmlFor={`${fieldName}-${propName}-type`}
                                >
                                  Property Type
                                </Label>
                                <Select
                                  defaultValue={propField.type}
                                  onValueChange={(value: any) => {
                                    const newProperties = {
                                      ...field.items?.properties,
                                    };
                                    newProperties[propName] = {
                                      ...newProperties[propName],
                                      type: value,
                                    };

                                    const newItems = {
                                      ...field.items,
                                      properties: newProperties,
                                    };

                                    onFieldUpdate(
                                      fieldName,
                                      field,
                                      "items",
                                      newItems,
                                    );
                                  }}
                                >
                                  <SelectTrigger
                                    id={`${fieldName}-${propName}-type`}
                                  >
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="string">
                                      String
                                    </SelectItem>
                                    <SelectItem value="number">
                                      Number
                                    </SelectItem>
                                    <SelectItem value="boolean">
                                      Boolean
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-2 mb-2">
                              <Label htmlFor={`${fieldName}-${propName}-title`}>
                                Display Label
                              </Label>
                              <Input
                                id={`${fieldName}-${propName}-title`}
                                defaultValue={propField.title || ""}
                                onBlur={(e) => {
                                  const newProperties = {
                                    ...field.items?.properties,
                                  };
                                  newProperties[propName] = {
                                    ...newProperties[propName],
                                    title: e.target.value,
                                  };

                                  const newItems = {
                                    ...field.items,
                                    properties: newProperties,
                                  };

                                  onFieldUpdate(
                                    fieldName,
                                    field,
                                    "items",
                                    newItems,
                                  );
                                }}
                                placeholder="Display label for the property"
                              />
                            </div>

                            <div className="flex items-center space-x-2">
                              <Switch
                                id={`${fieldName}-${propName}-required`}
                                checked={propField.required || false}
                                onCheckedChange={(checked) => {
                                  const newProperties = {
                                    ...field.items?.properties,
                                  };
                                  newProperties[propName] = {
                                    ...newProperties[propName],
                                    required: checked,
                                  };

                                  const newItems = {
                                    ...field.items,
                                    properties: newProperties,
                                  };

                                  onFieldUpdate(
                                    fieldName,
                                    field,
                                    "items",
                                    newItems,
                                  );
                                }}
                              />
                              <Label
                                htmlFor={`${fieldName}-${propName}-required`}
                              >
                                Required property
                              </Label>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 border rounded-md">
                      <p className="text-muted-foreground mb-2">
                        No properties defined yet
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newProp = {
                            name: "property",
                            type: "string",
                            title: "New Property",
                          };

                          const newItems = {
                            ...field.items,
                            properties: { property: newProp },
                          };

                          onFieldUpdate(fieldName, field, "items", newItems);
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" /> Add First Property
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {field.items?.type && field.items.type !== "object" && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="font-medium mb-2">Item Settings</h4>

                  {field.items.type === "string" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`${fieldName}-items-minLength`}>
                          Min Length
                        </Label>
                        <Input
                          id={`${fieldName}-items-minLength`}
                          type="number"
                          defaultValue={field.items.validation?.minLength || ""}
                          onBlur={(e) => {
                            const value = e.target.value
                              ? parseInt(e.target.value)
                              : undefined;
                            const newItems = {
                              ...field.items,
                              validation: {
                                ...field.items.validation,
                                minLength: value,
                              },
                            };
                            handleFieldUpdate(
                              fieldName,
                              field,
                              "items",
                              newItems,
                            );
                          }}
                          placeholder="Minimum length"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${fieldName}-items-maxLength`}>
                          Max Length
                        </Label>
                        <Input
                          id={`${fieldName}-items-maxLength`}
                          type="number"
                          defaultValue={field.items.validation?.maxLength || ""}
                          onBlur={(e) => {
                            const value = e.target.value
                              ? parseInt(e.target.value)
                              : undefined;
                            const newItems = {
                              ...field.items,
                              validation: {
                                ...field.items.validation,
                                maxLength: value,
                              },
                            };
                            handleFieldUpdate(
                              fieldName,
                              field,
                              "items",
                              newItems,
                            );
                          }}
                          placeholder="Maximum length"
                        />
                      </div>
                    </div>
                  )}

                  {field.items.type === "number" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`${fieldName}-items-minimum`}>
                          Minimum
                        </Label>
                        <Input
                          id={`${fieldName}-items-minimum`}
                          type="number"
                          defaultValue={field.items.validation?.minimum || ""}
                          onBlur={(e) => {
                            const value = e.target.value
                              ? parseFloat(e.target.value)
                              : undefined;
                            const newItems = {
                              ...field.items,
                              validation: {
                                ...field.items.validation,
                                minimum: value,
                              },
                            };
                            handleFieldUpdate(
                              fieldName,
                              field,
                              "items",
                              newItems,
                            );
                          }}
                          placeholder="Minimum value"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`${fieldName}-items-maximum`}>
                          Maximum
                        </Label>
                        <Input
                          id={`${fieldName}-items-maximum`}
                          type="number"
                          defaultValue={field.items.validation?.maximum || ""}
                          onBlur={(e) => {
                            const value = e.target.value
                              ? parseFloat(e.target.value)
                              : undefined;
                            const newItems = {
                              ...field.items,
                              validation: {
                                ...field.items.validation,
                                maximum: value,
                              },
                            };
                            handleFieldUpdate(
                              fieldName,
                              field,
                              "items",
                              newItems,
                            );
                          }}
                          placeholder="Maximum value"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default FieldEditor;
